import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core'
import { ControlValueAccessor, FormBuilder, NG_VALUE_ACCESSOR } from '@angular/forms'
import { Waitlist } from '@mmx/shared'
import { isEqual } from 'lodash'
import moment from 'moment'
import { filter, map, Observable, startWith, Subject, takeUntil, tap } from 'rxjs'

interface WeekdayTimeValue<T = string> {
  value: T
  label: string
  isDisabled?: boolean
}

interface TimePickedEvent {
  weekday: WeekdayTimeValue
  time: WeekdayTimeValue<Waitlist.WeekdayTimeType>
}

@Component({
  selector: 'mmx-availability-picker',
  templateUrl: './availability-picker.component.html',
  styleUrls: ['./availability-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AvailabilityPickerComponent,
      multi: true,
    },
  ],
})
export class AvailabilityPickerComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input()
  public locationsOpen: Record<string, Waitlist.WeekdayTimeType[]> = {}

  @Output()
  public availabilityChange = new EventEmitter<Waitlist.PatientAvailability>()

  public availabilityControl = this.fb.control<Waitlist.PatientAvailability>({})

  public weekdays: Array<{value: string, label: string}> = moment.weekdays().map((day) => ({
    value: day.substring(0, 3).toLowerCase(),
    label: `schedule.${day.substring(0, 3).toLowerCase()}`,
  }))

  public times: WeekdayTimeValue<Waitlist.WeekdayTimeType>[] = [
    {
      value: Waitlist.WeekdayTimeType.MORNING,
      label: 'availability-picker.times.morning',
    },
    {
      value: Waitlist.WeekdayTimeType.AFTERNOON,
      label: 'availability-picker.times.afternoon',
    },
    {
      value: Waitlist.WeekdayTimeType.EVENING,
      label: 'availability-picker.times.evening',
    },
    {
      value: Waitlist.WeekdayTimeType.ALL_DAY,
      label: 'availability-picker.times.allDay',
    },
  ]

  private onChanges$ = new Subject<void>()

  public availableTimes$: Observable<Record<string, WeekdayTimeValue<Waitlist.WeekdayTimeType>[]>> = this.onChanges$
    .pipe(
      startWith(this.locationsOpen),
      filter(() => !!this.locationsOpen),
      tap(() => this.removeDisabledTimes()),
      map(() => this.getAvailableTimes()),
    )

  private onChange!: (value: Waitlist.PatientAvailability | null) => void
  private onTouched!: () => void
  private onDestroy$ = new Subject<void>()

  constructor(
    private fb: FormBuilder,
  ) { }

  public ngOnInit() {
    moment.locale('en')

    this.availabilityControl.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe((value) => {
        if (!this.onChange) {
          return
        }

        this.onChange(value)
      })
  }

  public ngOnChanges() {
    this.onChanges$.next()
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public writeValue(value: Waitlist.PatientAvailability | null) {
    if (!value) {
      this.availabilityControl.setValue(this.getWeekdaysDefaultValue())
      return
    }

    this.availabilityControl.setValue(value)
  }

  public onTimePicked({ weekday, time }: TimePickedEvent) {
    const selectedTimes = this.availabilityControl.value?.[weekday.value] || []

    this.availabilityControl.patchValue({
      ...this.availabilityControl.value,
      [weekday.value]: this.getSelectedWeekdayTimes(selectedTimes, time.value),
    })

    this.availabilityChange.emit(this.availabilityControl.value || {})
  }

  public isWeekdaySelected(weekday: string) {
    return !!this.availabilityControl.value?.[weekday]?.length
  }

  public isTimeSelected(weekday: string, time: Waitlist.WeekdayTimeType) {
    return this.availabilityControl.value?.[weekday]?.includes(time)
  }

  public registerOnChange(fn: any) {
    this.onChange = fn
  }

  public registerOnTouched(fn: any) {
    this.onTouched = fn
  }

  private getWeekdaysDefaultValue(): Waitlist.PatientAvailability {
    return this.weekdays.reduce((acc, day) => ({
      ...acc,
      [day.value]: [],
    }), {})
  }

  private getSelectedWeekdayTimes(weekdayTimes: Waitlist.WeekdayTimeType[], time: Waitlist.WeekdayTimeType) {
    // this time is already selected so remove it
    if (weekdayTimes.includes(time)) {
      return weekdayTimes.filter((t: string) => t !== time)
    }

    // all-day is selected so remove other times and add only this one
    if (time === Waitlist.WeekdayTimeType.ALL_DAY) {
      return [time]
    }

    // if this is the third time selected and all-day is not one of them
    // remove all selected times and add this one
    if (weekdayTimes.length === 2 && !weekdayTimes.includes(Waitlist.WeekdayTimeType.ALL_DAY)) {
      return [Waitlist.WeekdayTimeType.ALL_DAY]
    }

    // add this time to the selected times
    // if all-day is selected, remove it
    return [...weekdayTimes.filter((t) => t !== Waitlist.WeekdayTimeType.ALL_DAY), time]
  }

  /**
   * Remove times that are not available for the selected locations
   */
  private removeDisabledTimes() {
    const selectedTimes = this.availabilityControl.value
    const weekdays = Object.keys(selectedTimes || {})

    const newSelectedTimes = weekdays.reduce((acc, weekday) => {
      const times = selectedTimes?.[weekday] || []

      return {
        ...acc,
        [weekday]: times.filter((time) => this.locationsOpen[weekday].includes(time)),
      }
    }, {})

    if (isEqual(selectedTimes, newSelectedTimes)) {
      return
    }

    this.availabilityControl.patchValue(newSelectedTimes)
  }

  /**
   * Get available times for each weekday based on the selected locations
   */
  private getAvailableTimes() {
    const weekdays = Object.keys(this.locationsOpen)

    return weekdays.reduce((acc, weekday) => {
      const availableTimes = this.locationsOpen[weekday]

      // if no locations are open on this weekday, disable all times
      if (!availableTimes.length) {
        return {
          ...acc,
          [weekday]: [],
        }
      }

      return {
        ...acc,
        [weekday]: this.times.map((time) => ({
          ...time,
          isDisabled: !availableTimes.includes(time.value),
        })),
      }
    }, {})
  }
}
